import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import axios from 'axios';

import { authorizedPdfList } from 'constants/authorizedPdfList';
import { INITIAL_PDF_DATA } from 'constants/initialPdfData';
import { CustomButton, CustomInput, Spinner } from 'components';
import { ExternalCalibration, ServiceStation } from 'interfaces';
import { authorizedValidationSchemeAgent } from 'validation';
import {
  externalCalibrationService,
  serialNumbersService,
  stationsService,
} from 'services';

import './GeneratePdfForm.scss';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { AnyObject, TestContext } from 'yup';
import { CustomValidity } from '../CustomValidity';

export const CreateAuthorizedForm = () => {
  const [errMessage, setErrMessage] = useState<string>('');
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
  const [fulfilledMessage, setFulfilledMessage] = useState<string>('');
  const [serviceStations, setServiceStations] = useState<[] | ServiceStation[]>(
    []
  );
  const [isPremium, setIsPremium] = useState<boolean>(false);

  const [fields] = useState(
    authorizedPdfList.filter((field) => {
      return !['email', 'produktionsdatum', 'gab-service-station'].includes(
        field.id
      );
    })
  );

  const navigate = useNavigate();

  const computerSnField = fields.find((o) => o.id === 'computerSn');

  const clearStates = () => {
    setErrMessage('');
    setIsRequestPending(false);
    setFulfilledMessage('');
  };

  const formik = useFormik({
    initialValues: INITIAL_PDF_DATA,
    onSubmit: async (values) => {
      clearStates();

      setIsRequestPending(true);

      try {
        if (!values.imo) values.imo = undefined;

        const calibration = await externalCalibrationService.create(values);
        setIsRequestPending(false);
        setFulfilledMessage('Data submitted');

        if (calibration) {
          navigate(
            `${ROUTES.documents_overview}/${calibration.sn}/${calibration.certificateNr}`
          );
        }
      } catch (e) {
        if (axios.isAxiosError(e) && e.response)
          setErrMessage(e.response.data.message);
        if (e instanceof Error) setErrMessage(e.message);
        setIsRequestPending(false);
      }
    },
    validationSchema: authorizedValidationSchemeAgent(
      async (value, ctx: TestContext<AnyObject>) => {
        const sn = value.toString();

        try {
          const serial = await serialNumbersService.findSerial(sn);

          if (!serial?.authorizedPdfAvailable) {
            if (computerSnField) computerSnField.disabled = true;

            return ctx.createError({
              message:
                'We could not issue a calibration certificate for your serial number.',
            });
          }

          if (computerSnField)
            computerSnField.disabled = serial.typ !== 'Messzelle';

          return true;
        } catch (e) {
          if (computerSnField) computerSnField.disabled = true;
          return false;
        }
      },
      false
    ),
  });

  useEffect(() => {
    (async () => {
      for (const field of fields) {
        field.disabled = false;
      }

      if (computerSnField) computerSnField.disabled = true;

      const response = await stationsService.getUserStations();

      setServiceStations(response);
      await formik.setFieldValue('serviceStationId', response[0].id);
      setIsPremium(response[0].premium);
    })();
  }, []);

  return (
    <div className="generate-pdf-container">
      <h1 className="generate-pdf__header">Enter your data here</h1>
      <form onSubmit={formik.handleSubmit}>
        <div className="generate-pdf__form-list">
          <div className="generate-pdf__select-box">
            <p>Service Station</p>
            <select
              name="serviceStationId"
              id="serviceStationId"
              className="generate-pdf__select"
              value={formik.values.serviceStationId}
              onChange={(e) => {
                formik.setFieldValue(
                  'serviceStationId',
                  Number(e.target.value)
                );
                setIsPremium(
                  serviceStations.find((o) => o.id === Number(e.target.value))
                    ?.premium || false
                );
              }}
              onBlur={formik.handleBlur}
            >
              {serviceStations.length &&
                serviceStations.map((serviceStation) => (
                  <option value={serviceStation.id} key={serviceStation.id}>
                    {serviceStation.company}
                  </option>
                ))}
            </select>
          </div>
          {fields.map(({ id, name, disabled, type }) => {
            const identifier = id as keyof ExternalCalibration;
            console.log(id, disabled);
            if (type === 'gab') return <div key={identifier}></div>;
            if (type === 'space')
              return (
                <div key={identifier} className="generate-pdf__space"></div>
              );

            if (id === 'validity')
              return (
                <div key={identifier}>
                  <CustomValidity
                    label={name}
                    disabledFiveYearly={false}
                    disabledMonthly={disabled}
                    width="100%"
                    showCheckbox={isPremium}
                    valueFiveYearly={formik.values['fiveYearly']}
                    valueMonthly={formik.values['monthly']}
                    onChangeFiveYearly={(e) => {
                      const fiveYearly = fields.find(
                        (o) => o.id === 'validity'
                      );
                      formik.handleChange(e);
                      if (fiveYearly) fiveYearly.disabled = e.target.checked;
                      if (e.target.checked) formik.setFieldValue('monthly', '');
                      else formik.setFieldValue('monthly', 12);
                    }}
                    onChangeMonthly={formik.handleChange}
                    onBlurMonthly={formik.handleBlur}
                    onBlurFiveYearly={formik.handleBlur}
                  />
                </div>
              );

            return (
              <div key={identifier}>
                <CustomInput
                  id={identifier}
                  name={identifier}
                  label={name}
                  disabled={disabled}
                  width="100%"
                  type={type}
                  value={formik.values[identifier]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched[identifier] && formik.errors[identifier] && (
                  <div className="generate-pdf__error-validation-box">
                    <p className="generate-pdf__error-validation-text">
                      {formik.errors[identifier]}
                    </p>
                  </div>
                )}
                {identifier === 'sn' &&
                  formik.touched[identifier] &&
                  !formik.errors[identifier] && (
                    <div className="generate-pdf__success-validation-box">
                      <p className="generate-pdf__success-validation-text">
                        Serial number is valid
                      </p>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
        {!isRequestPending && (
          <div className="generate-pdf__button-box">
            <CustomButton type="submit" text="Save calibration" width="35%" />
          </div>
        )}
        {isRequestPending && (
          <div className="generate-pdf__spinner-box">
            <Spinner />
          </div>
        )}
      </form>
      {errMessage && (
        <div className="generate-pdf__error-box">
          <p className="generate-pdf__error-text">{errMessage}</p>
        </div>
      )}
      {fulfilledMessage && (
        <div className="generate-pdf__success-box">
          <p className="generate-pdf__success-text">{fulfilledMessage}</p>
        </div>
      )}
    </div>
  );
};
